import { Avatar, Button } from "@mantine/core";
import { IconLogout, IconPlus } from "@tabler/icons-react";
import { useEffect, useState } from "react";

import { authClient } from "@/lib/auth-clients";
import { orpcClient } from "@/lib/orpc-client";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface UserData {
  id: string;
  name: string;
  email: string;
  image?: string | null;
  emailVerified: boolean;
}

interface CompanyData {
  id: string;
  organizationId: string;
  name: string;
  email?: string | null;
  logo?: string | null;
}

function CompanyListPage() {
  const { t } = useTranslation();
  const [organizations, setOrganizations] = useState<CompanyData[]>([]);
  const [userData, setUserData] = useState<UserData | null>(null);
  const navigate = useNavigate();

  const handleOnCompanyClicked = async (organizationId: string) => {
    try {
      await authClient.organization.setActive({ organizationId });
      navigate(`/hrMainPage/companyInfo`);
    } catch (error) {
      console.error("Error setting active organization:", error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const sessionData = await authClient.getSession();
        if (!sessionData || !sessionData.data) {
          window.localStorage.clear();
          navigate(`/login`);
          return;
        }

        setUserData(sessionData.data.user);

        const companies = await orpcClient.company.list({});
        setOrganizations(companies);
      } catch (error) {
        console.error("Error fetching data:", error);
        window.localStorage.clear();
        navigate(`/login`);
      }
    };

    fetchData();
  }, [navigate]);

  return (
    <div className="flex h-full items-center justify-center px-8">
      <div
        className="rounded-xl bg-white px-8 py-8"
        style={{ minHeight: "700px", height: "100%", marginTop: "100px" }}
      >
        <div className="flex justify-between">
          <div className="text-5xl font-bold" style={{ color: "#6F6F6F" }}>
            {t("companyList.title")}
          </div>
          <div className="flex items-center cursor-pointer">
            <div
              onClick={async () => {
                await authClient.signOut();
                window.localStorage.clear();
                navigate(`/login`);
              }}
              className="mr-1 text-lg text-gray-800"
            >
              {t("companyList.backToLogin")}
            </div>
            <IconLogout />
          </div>
        </div>
        <div className="py-4 text-base" style={{ color: "#6F6F6F" }}>
          {t("companyList.selectCompany")}
        </div>
        <div className="py-2 text-base" style={{ color: "#6F6F6F" }}>
          <div>ID: {userData?.id}</div>
          <div>Email: {userData?.email}</div>
        </div>
        <div className="flex justify-between py-4">
          <div>
            <Button variant="filled" color="gray" style={{ margin: "0.75rem" }}>
              {t("companyList.recentlyOpened")}
            </Button>
            <Button variant="filled" color="gray" style={{ margin: "0.75rem" }}>
              {t("companyList.yourCompanies")}
            </Button>
          </div>
          <Button
            leftSection={<IconPlus size={14} />}
            variant="outline"
            color="black"
            radius="xl"
            style={{ margin: "0.75rem" }}
            onClick={() => navigate(`/addCompanyListPage`)}
          >
            {t("companyList.addCompany")}
          </Button>
        </div>
        <div>
          <div className="grid grid-cols-2 gap-8">
            {organizations?.map((org) => {
              return (
                <div
                  key={org.id}
                  className="overflow-hidden rounded border-2 border-gray-400 p-1 shadow cursor-pointer"
                  style={{ backgroundColor: "#ECECEC" }}
                  onClick={() => handleOnCompanyClicked(org.organizationId)}
                >
                  <div className="grid grid-cols-10">
                    <div className="col-span-3">
                      {org.logo ? (
                        <img
                          src={org.logo}
                          alt="companyImage"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <Avatar size="xl" name={org.name} />
                      )}
                    </div>
                    <div className="col-span-7 flex flex-col justify-between p-4">
                      <div className="text-left">
                        <p>Name: {org.name}</p>
                      </div>
                      <div className="">
                        <p>Email: {org.email}</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CompanyListPage;
